import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { PE_DELEGATED_SHELL_UI_ENABLED, clearPeDelegatedChildTenantId } from '@/services/peDelegatedContext';
import { HorizontalNavigation } from './HorizontalNavigation';
import { SidebarNavigation } from '../navigation/SidebarNavigation';
import { BreadcrumbNavigation } from '../navigation/BreadcrumbNavigation';
import { CommandPalette } from '../navigation/CommandPalette';
import { NavigationItem } from '@/types/navigation';
import { NotificationBanner } from '@/components/notifications/NotificationBanner';
import { PeDelegatedReadOnlyBanner } from '@/components/pe-portfolio/PeDelegatedReadOnlyBanner';
import { AssessmentPreviewProvider } from '@/components/assessment-engine/preview';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { OperationsPaneProvider } from '@/components/operations/OperationsPaneProvider';
import { OperationsHost } from '@/components/operations/OperationsHost';

export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Partially collapsed (icons only) by default
  const [sidebarHovered, setSidebarHovered] = useState(false); // Track hover state for auto-expand
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<NavigationItem[]>([]);
  const [recentItems, setRecentItems] = useState<NavigationItem[]>([]);
  const [, setIsLoading] = useState(true);
  const commandPalette = useCommandPalette();

  // When delegated shell is frozen, drop persisted child + peContext query so API calls stay PE-firm scoped.
  useEffect(() => {
    if (!PE_DELEGATED_SHELL_UI_ENABLED) {
      clearPeDelegatedChildTenantId();
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('peContext')) {
          url.searchParams.delete('peContext');
          const q = url.searchParams.toString();
          window.history.replaceState({}, '', url.pathname + (q ? `?${q}` : '') + url.hash);
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  // Load navigation data on mount
  useEffect(() => {
    const loadNavigationData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          console.warn('No authentication token available for navigation data');
          setFavorites([]);
          setRecentItems([]);
          return;
        }

        setIsLoading(true);
        // Temporarily disable API calls until endpoints are working
        // const [favoritesData, recentData] = await Promise.all([
        //   navigationService.getFavorites(),
        //   navigationService.getRecentNavigation()
        // ]);
        
        // setFavorites(favoritesData.favorites);
        // setRecentItems(recentData.recent_items);
        setFavorites([]);
        setRecentItems([]);
      } catch (error) {
        console.error('Failed to load navigation data:', error);
        setFavorites([]);
        setRecentItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadNavigationData();
  }, []);

  // Handle navigation
  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Handle sidebar toggle (collapse/expand on desktop)
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };


  return (
    <OperationsPaneProvider>
    <div className="min-h-screen bg-[var(--color-primary-bg)]">
      {/* Top Navigation - Full Width, Fixed at Top */}
      <div className="fixed top-0 left-0 right-0 z-50 w-full">
        <HorizontalNavigation 
          onMobileMenuToggle={handleMobileMenuToggle}
          onSidebarToggle={handleSidebarToggle}
        />
      </div>

      {/* Content Area Below Navbar */}
      <div className="flex pt-[56px] min-h-screen">
        {/* Desktop Sidebar - Fixed, always visible, can collapse/expand */}
        <div
          onMouseEnter={() => setSidebarHovered(true)}
          onMouseLeave={() => setSidebarHovered(false)}
          className="hidden md:block"
        >
          <SidebarNavigation
            isCollapsed={sidebarCollapsed && !sidebarHovered}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            favorites={favorites}
            recentItems={recentItems}
            onNavigate={handleNavigate}
            isMobile={false}
            isDesktopOpen={true}
          />
        </div>

        {/* Mobile Sidebar */}
        <SidebarNavigation
          isCollapsed={false}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          favorites={favorites}
          recentItems={recentItems}
          onNavigate={handleNavigate}
          isMobile={true}
          isMobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />

        {/* Main Content - With margin for fixed sidebar */}
        <div className={`flex-1 flex flex-col min-w-0 min-h-0 h-[calc(100vh-56px)] transition-all duration-300 ${(sidebarCollapsed && !sidebarHovered) ? 'md:ml-14' : 'md:ml-64'}`}>
          {/* Notification Banner */}
          <NotificationBanner />
          <PeDelegatedReadOnlyBanner />

          {/* Breadcrumbs */}
          <div>
            <BreadcrumbNavigation />
          </div>

          {/* Page content + Operations dock (shifts the page, does not overlay) */}
          <div className="flex flex-1 min-h-0 min-w-0">
            <main className="flex-1 min-w-0 p-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] overflow-auto">
              <AssessmentPreviewProvider>
                <Outlet />
              </AssessmentPreviewProvider>
            </main>
            <OperationsHost />
          </div>

        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette 
        isOpen={commandPalette.isOpen}
        onClose={commandPalette.close}
      />
    </div>
    </OperationsPaneProvider>
  );
}; 